import { createNextState, isFulfilled, isRejectedWithValue } from '@reduxjs/toolkit';

import type { AnyApi } from '../buildCreateApi';
import type { WritableDraft } from '../immer-types';
import type { EndpointDefinitions, TagDescription } from '../rtk-query-types';

import type { InvalidateTagsAction } from './actions/createInvalidateTagsActionMatcher';
import type { QueryFulfilledAction } from './actions/createQueryFulfilledActionMatcher';
import type { QueryRejectedAction } from './actions/createQueryRejectedActionMatcher';
import type { MutationFulfilledAction } from './actions/createMutationFulfilledActionMatcher';
import type { MutationRejectedAction } from './actions/createMutationRejectedActionMatcher';

import type { AnyApiState } from './types';

/**
 * Сохраняет переданные инвалидационные теги во всех задетых запросах.
 * @param api Инстанс RTK Query API.
 * @param draft Черновик нового стейта.
 * @param tags Список инвалидационных тегов.
 */
function setInvalidatedBy<Api extends AnyApi>(
    api: Api,
    draft: WritableDraft<AnyApiState>,
    tags: readonly TagDescription<string>[]
) {
    // Вычисляем все запросы, которые будут инвалидированы переданными тегами
    const invalidatedBy = api.util.selectInvalidatedBy({ [api.reducerPath]: draft }, tags);

    for (const { queryCacheKey } of invalidatedBy) {
        const querySubState = draft.queries[queryCacheKey];

        if (querySubState) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error Добавляем поле `invalidatedBy`, которого нет в типах
            querySubState.invalidatedBy = tags;
        }
    }
}

/**
 * Сохраняет инвалидационные теги из экшена api.util.invalidateTags во всех задетых запросах.
 * @param state Стейт.
 * @param action Экшен invalidateTags.
 * @param api Инстанс RTK Query API.
 * @returns Стейт с сохраненными тегами.
 */
export function addInvalidatedByFromAction<Api extends AnyApi>(
    state: AnyApiState,
    action: InvalidateTagsAction,
    api: Api
) {
    return createNextState(state, draft => setInvalidatedBy(api, draft, action.payload));
}

/**
 * Сохраняет инвалидационные теги после завершения мутации во всех задетых запросах.
 * @param state Стейт.
 * @param action Экшен завершения мутации (executeMutation/fulfilled или executeMutation/rejected).
 * @param api Инстанс RTK Query API.
 * @param definitions Параметры RTK Query эндпоинтов.
 * @returns Стейт с сохраненными тегами.
 */
export function addInvalidatedByFromMutation<
    State extends AnyApiState,
    Api extends AnyApi,
    Definitions extends EndpointDefinitions,
>(state: State, action: MutationFulfilledAction | MutationRejectedAction, api: Api, definitions: Definitions) {
    const invalidatesTags = definitions[action.meta.arg.endpointName]?.invalidatesTags;

    if (!invalidatesTags) {
        return state;
    }

    // invalidatesTags может быть функцией, которая принимает результаты выполнения мутации, ошибку и аргументы
    if (typeof invalidatesTags === 'function') {
        const tags: readonly TagDescription<string>[] = invalidatesTags(
            // Передаем результат выполнения запроса, только если он успешен
            isFulfilled(action) ? action.payload : undefined,
            // Передаем ошибку выполнения запроса, только если она произошла
            isRejectedWithValue(action) ? action.payload : undefined,
            action.meta.arg.originalArgs,
            action.meta.baseQueryMeta
        );

        return createNextState(state, draft => setInvalidatedBy(api, draft, tags));
    }

    // Или же invalidatesTags может быть просто массивом тегов
    if (Array.isArray(invalidatesTags)) {
        return createNextState(state, draft => setInvalidatedBy(api, draft, invalidatesTags));
    }

    return state;
}

/**
 * Удаляет поле `invalidatedBy` из результатов запроса
 * @param state Стейт.
 * @param action Экшен завершения запроса (executeQuery/fulfilled или executeQuery/rejected)
 * @returns Стейт с удаленным `invalidatedBy` в текущем запросе
 */
export function removeInvalidatedBy(state: AnyApiState, action: QueryFulfilledAction | QueryRejectedAction) {
    return createNextState(state, draft => {
        const isRejectedByCondition = 'condition' in action.meta && action.meta.condition;

        // Если запрос был прерван (например из-за того, что данные уже есть в кеше), то ничего не делаем
        if (isRejectedByCondition) {
            return;
        }

        const querySubState = draft.queries[action.meta.arg.queryCacheKey];

        if (querySubState) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error Удаляем поле `invalidatedBy`, которого нет в типах
            delete querySubState.invalidatedBy;
        }
    });
}
